<template>
    <div class="card py-8 px-4 lg:py-16 lg:px-8">

        <!-- confirmation popup  -->
        <speakol-confirmation-popup
            :showConfirmationPopup="showConfirmationPopup"
            title="Confirmation"
            cancelText="Cancel"
            confirmationText="Remove"
            v-on:confirm="deleteSelectedAudience"
            v-on:hide-confirmation-popup="showConfirmationPopup = false"
        >
            <template v-slot:body>
                Are you sure you want to remove <strong>{{ selectedAudiencesCount }}
                <span v-if="selectedAudiencesCount == 1">audience </span>
                <span v-else>audiences </span>

                </strong>from your saved list ?
            </template>
        </speakol-confirmation-popup>


        <!-- Toaster -->
        <SpeakolNotificationToaster
          :show="isToasterVisible"
          :message="toasterText"
          :icon="saveIcon"
          closeButtonText="X"
          @close="isToasterVisible = false"
        />

        <!-- header and description -->
        <div class="title lg:title w-full">
          <span>{{ $t('Saved Audiences') }}</span>
        </div>

        <div class="lg:flex lg:items-baseline">


          <div class="description lg:description mb-8 lg:mb-0 lg:mr-4">
              You can create new audience, check your previously saved targeted ones, edit or remove any of them.
          </div>


          <div class="lg:flex lg:justify-end lg:w-full">

            <!-- filter -->
            <speakol-input
                class="w-full mb-8 lg:mb:0 lg:search lg:mr-4"
                placeholder="Audience Name"
                icon="speakol-search"
                v-model="filters.searchable"
            />

            <!-- buttons -->
            <button type="border" class="btn search-btn w-full mb-8 lg:mb-0 lg:search-btn lg:mr-4" @click="search()">Search</button>
            <button class="btn create-btn w-full mb-8 lg:mb-0 lg:create-btn" @click="goToCreate()">Create New Audience</button>

          </div>
        </div>

        <!-- delete selected section -->
        <div v-if="selectedAudiencesCount > 0" class="w-full flex flex-wrap lg:flex-no-wrap mb-8">
            <div class="flex mb-6 lg:mb-0 items-center">
                <div class="mr-2 selected-audiences-count whitespace-no-wrap">
                    {{ selectedAudiencesCount }} Selected audiences
                </div>
                <div class="remove-selected-audience speakol speakol-cancel cursor-pointer transform" @click="clearSelection"></div>
                <div class="vl lg:mr-6 invisible lg:ml-6 lg:visible"></div>
            </div>

            <div class="flex cursor-pointer w-full" @click="handleRemoveAllAudienceClicked">
                <vs-icon class="mr-5" size="20px" icon="speakol-remove" color="#DB2828" icon-pack="speakol" round></vs-icon>
                <div class="whitespace-no-wrap delete-selected-text">{{ $t('Remove all selected audiences') }}</div>
            </div>
        </div>

        <!-- table -->
        <a-g-speakol-table
            class="audinces-table"
            ref="audiencesTable"
            :filters="agFilters"
            :columns="agColumns"
            :options="agOptions"
            @selectionChanged="handleSelectionChange"
            @paginationChanged="handlePaginationChange"
        />

    </div>
</template>

<script>

import moment from 'moment'
import {map} from 'lodash'
import AGSpeakolTable from '@/components/AGSpeakolTable'
import AudiencesDataSource from '@/datasources/AudiencesDataSource'
import CheckboxHeader from '@/components/AGSpeakolTable/AgCheckboxHeader'
import SpeakolNotificationToaster from '@/components/SpeakolNotificationToaster'
import ActionColumn from './action-column'
import LocationsColumn from './locations-column'

export default {
  components: {
    AGSpeakolTable,
    CheckboxHeader,
    ActionColumn,
    LocationsColumn,
    SpeakolNotificationToaster
  },
  data () {
    const self = this
    return {
      // Toaster //
      saveIcon: require('@/assets/images/success.svg'),
      isToasterVisible: false,
      toasterText: '',

      //   filters //
      filters: {
        searchable: null
      },

      //   AgGrid Section //
      agFilters: {
        searchable: null
      },
      agOptions: {
        dataSource: AudiencesDataSource,
        rowSelection: 'multiple',
        maxBlocksInCache: 1,
        context: {
          parent: self
        }
      },
      agColumns: [
        {
          headerName: '',
          minWidth: 30,
          maxWidth: 55,
          resizable: false,
          headerCheckboxSelection: true,
          headerCheckboxSelectionFilteredOnly: false,
          checkboxSelection: true,
          colId: 'headerCheckbox',
          headerComponentFramework: 'CheckboxHeader'
        },
        {
          headerName: 'Name',
          field: 'name',
          minWidth: 140,
          sortable: true
        },
        {
          headerName: 'Date',
          field: 'created_at',
          minWidth: 155,
          sortable: true,
          valueGetter (params) {
            if (params.data) {
              return moment(params.data.date).format('MMMM DD, YYYY')
            }
          }
        },
        {
          headerName: 'Location',
          minWidth: 140,
          cellRendererFramework: 'LocationsColumn'
        },
        {
          headerName: 'Devices',
          field: 'devices',
          minWidth: 155,
          valueGetter (params) {
            return params.data && params.data.devices && params.data.devices.length !== 0 ? params.data.devices.join(', ') : '-'
          }
        },
        {
          headerName: 'Actions',
          minWidth: 175,
          cellRendererFramework: 'ActionColumn'
        }
      ],

      // selection options //
      selectedAudiencesCount: 0,
      selectedAudiences: [],

      showConfirmationPopup: false

    }
  },


  beforeMount () {
    this.$on('AUDIENCE_DELETED', (name) => {
      this.applyFilters()
      this.changeToasterText(`${name} has been removed`)
      if (this.$refs['audiencesTable'].getTotalRows() === 1) {
        location.reload()
      }
      this.showToaster()
    })
  },

  methods: {

    // Toaster Methods //
    showToaster () {
      this.isToasterVisible = true
      setTimeout(() => {
        this.isToasterVisible = false
      }, 4000)
    },

    changeToasterText (text) {
      this.toasterText = text
    },

    goToCreate () {
      this.$router.push('/targeting/audiences/create')
    },
    search () {
      this.applyFilters()

    },
    applyFilters () {
      this.agFilters.searchable = this.filters.searchable
      this.$nextTick(() => {
        this.$refs['audiencesTable'].applyFilters()
      })
    },
    async deleteSelectedAudience () {
      this.showConfirmationPopup = false
      const { data } = await window.axios.delete('/audience', {
        data: {
          audiences_ids: map(this.selectedAudiences, 'id')
        }
      })
      const isDeleted =  data && data.payload
      if (isDeleted) {
        const toasterText = this.selectedAudiencesCount === 1 ? 'Audience has been removed' : 'Audiences have been removed.'
        this.$refs['audiencesTable'].applyFilters()
        this.changeToasterText(toasterText)
        this.showToaster()
      }
      this.clearSelection()
    },
    clearSelection () {
      this.isAudienceSelected = false
      this.selectedAudiencesCount = 0
      this.$refs['audiencesTable'].clearSelection()
    },

    // selsection change events //
    handlePaginationChange () {
      this.handleSelectionChange([])
    },

    handleSelectionChange (selectedNodes) {
      this.selectedAudiencesCount = selectedNodes.length || 0
      this.selectedAudiences = selectedNodes || []
    },

    handleRemoveAllAudienceClicked () {
      this.showConfirmationPopup = true
    }

  }
}
</script>


<style lang="scss"  scoped>
  .card {
    background: #fff;
  }

  .remove-selected-audience {
    color: #2c2c2c;
  }

  .title {
    font-style: normal;
    font-weight: 500;
    font-size: 18px;
    line-height: 27px;
    color: #343637;
  }

  .description {
    font-style: normal;
    font-weight: normal;
    font-size: 14px;
    line-height: 21px;
    color: #999;
  }

  .search-btn {
    background: #fff;
    border: 1px solid #0092d7;
    border-radius: 3px;
    color: #0092d7;
    cursor: pointer;
  }

  .create-btn {
    border-radius: 3px;
    background: #0092d7;
    border: 1px solid #0092d7;
    color: white;
    cursor: pointer;
  }

  .btn {
    height: 40px;
    font-style: normal;
    font-weight: 500;
    font-size: 14px;
    line-height: 21px;
    text-align: center;
  }

  .vl {
    height: 25px;
    border-left: 1px solid #000;
  }

  .selected-audiences-count {
    font-style: normal;
    font-weight: normal;
    font-size: 16px;
    line-height: 26px;
    letter-spacing: 0.1px;
    color: #2c2c2c;
  }

  .delete-selected-text {
    font-style: normal;
    font-weight: 500;
    font-size: 14px;
    line-height: 21px;
    color: #db2828;
  }

  @media (min-width: 768px) {
    .lg\:title {
      font-style: normal;
      font-weight: 500;
      font-size: 24px;
      line-height: 18px;
      color: #343637;
    }

    .lg\:description {
      font-style: normal;
      font-weight: normal;
      font-size: 14px;
      line-height: 21px;
      color: #999;
    }

    .lg\:search {
      width: 280px !important;
    }

    .lg\:search-btn {
      width: 135px !important;
    }

    .lg\:create-btn {
      width: 215px !important;
    }
  }
</style>
